/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CursorPaginatedResponseFormApprovalConfigListItemDtov1 } from '../models/cursor-paginated-response-form-approval-config-list-item-dtov-1';
import { CursorPaginationRequest } from '../models/cursor-pagination-request';
import { FormApprovalConfigUpdateCommandDtov1 } from '../models/form-approval-config-update-command-dtov-1';
import { FormApprovalConfigV1 } from '../models/form-approval-config-v-1';


/**
 * API for managing section-based approval configuration per form
 */
@Injectable({
  providedIn: 'root',
})
export class FormApprovalConfigV1Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getFormApprovalConfigV1
   */
  static readonly GetFormApprovalConfigV1Path = '/v1/form-approval-configs/{formId}';

  /**
   * Get approval config for a form.
   *
   * Returns the section-based approval configuration for the given form. Config is auto-created when the form is created (if sectionBasedReviewEnabled). Returns 404 if no config exists for this form.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFormApprovalConfigV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFormApprovalConfigV1$Response(params: {
    formId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<FormApprovalConfigV1>> {

    const rb = new RequestBuilder(this.rootUrl, FormApprovalConfigV1Service.GetFormApprovalConfigV1Path, 'get');
    if (params) {
      rb.path('formId', params.formId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FormApprovalConfigV1>;
      })
    );
  }

  /**
   * Get approval config for a form.
   *
   * Returns the section-based approval configuration for the given form. Config is auto-created when the form is created (if sectionBasedReviewEnabled). Returns 404 if no config exists for this form.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFormApprovalConfigV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFormApprovalConfigV1(params: {
    formId: string;
  },
  context?: HttpContext

): Observable<FormApprovalConfigV1> {

    return this.getFormApprovalConfigV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<FormApprovalConfigV1>) => r.body as FormApprovalConfigV1)
    );
  }

  /**
   * Path part for operation updateFormApprovalConfigV1
   */
  static readonly UpdateFormApprovalConfigV1Path = '/v1/form-approval-configs/{formId}';

  /**
   * Update approval config for a form.
   *
   * Replaces the section-based approval configuration for the given form. Stage is derived server-side from page name suffix ('_internal' → INITIAL, '_external' → FINAL). List order within each stage determines sequential review order.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFormApprovalConfigV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFormApprovalConfigV1$Response(params: {
    formId: string;

    /**
     * Updated approval configuration
     */
    body: FormApprovalConfigUpdateCommandDtov1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<FormApprovalConfigV1>> {

    const rb = new RequestBuilder(this.rootUrl, FormApprovalConfigV1Service.UpdateFormApprovalConfigV1Path, 'put');
    if (params) {
      rb.path('formId', params.formId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FormApprovalConfigV1>;
      })
    );
  }

  /**
   * Update approval config for a form.
   *
   * Replaces the section-based approval configuration for the given form. Stage is derived server-side from page name suffix ('_internal' → INITIAL, '_external' → FINAL). List order within each stage determines sequential review order.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateFormApprovalConfigV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFormApprovalConfigV1(params: {
    formId: string;

    /**
     * Updated approval configuration
     */
    body: FormApprovalConfigUpdateCommandDtov1
  },
  context?: HttpContext

): Observable<FormApprovalConfigV1> {

    return this.updateFormApprovalConfigV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<FormApprovalConfigV1>) => r.body as FormApprovalConfigV1)
    );
  }

  /**
   * Path part for operation listFormApprovalConfigsCursorV1
   */
  static readonly ListFormApprovalConfigsCursorV1Path = '/v1/form-approval-configs/cursor-list';

  /**
   * List approval configs using cursor-based pagination.
   *
   * Retrieves non-deleted approval configs with form summary details using continuation token pagination. Supports filtering and sorting. Maximum page size is 200.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listFormApprovalConfigsCursorV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listFormApprovalConfigsCursorV1$Response(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CursorPaginatedResponseFormApprovalConfigListItemDtov1>> {

    const rb = new RequestBuilder(this.rootUrl, FormApprovalConfigV1Service.ListFormApprovalConfigsCursorV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CursorPaginatedResponseFormApprovalConfigListItemDtov1>;
      })
    );
  }

  /**
   * List approval configs using cursor-based pagination.
   *
   * Retrieves non-deleted approval configs with form summary details using continuation token pagination. Supports filtering and sorting. Maximum page size is 200.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listFormApprovalConfigsCursorV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listFormApprovalConfigsCursorV1(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<CursorPaginatedResponseFormApprovalConfigListItemDtov1> {

    return this.listFormApprovalConfigsCursorV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<CursorPaginatedResponseFormApprovalConfigListItemDtov1>) => r.body as CursorPaginatedResponseFormApprovalConfigListItemDtov1)
    );
  }

}
